import { fetchWithTimeout } from './fetchWithTimeout';
import { getApiBaseUrl, SALON_API_PREFIX } from './apiConfig';
import { captureReportPageImages } from '../../shared/captureReportPageImages';
import { franchiseAuthHeaders, hasFranchiseFlowAuth } from '../../shared/franchiseFlowAuth';

function absolutizeUrl(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = String(window.location.origin).replace(/\/$/, '');
    return s.startsWith('/') ? `${origin}${s}` : `${origin}/${s}`;
  }
  return s;
}

/** Prefer token + current API base so QR never points at brand /api/salon (login-gated). */
function resolveSalonReportPdfDownloadUrl(data, apiBase) {
  const base = String(apiBase || getApiBaseUrl() || '').replace(/\/$/, '');
  const token = typeof data?.token === 'string' ? data.token.trim() : '';
  if (token && base) {
    return absolutizeUrl(`${base}${SALON_API_PREFIX}/report-pdf/${encodeURIComponent(token)}`);
  }
  if (token) {
    return absolutizeUrl(`${SALON_API_PREFIX}/report-pdf/${encodeURIComponent(token)}`);
  }
  const url = data?.download_url;
  if (!url) throw new Error('PDF download link missing');
  return absolutizeUrl(url);
}

function pdfQrFetchHeaders(extra = {}) {
  const base = { 'Content-Type': 'application/json', ...extra };
  if (hasFranchiseFlowAuth()) {
    return franchiseAuthHeaders(base);
  }
  return base;
}

/** Structured professional PDF via ReportLab (matches Skin Routine sections). */
export async function createSalonReportPdfDownloadUrl(payload, { timeoutMs = 120000, headers } = {}) {
  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';
  const response = await fetchWithTimeout(
    `${API_BASE_URL}${SALON_API_PREFIX}/generate-report-pdf-qr`,
    {
      method: 'POST',
      headers: pdfQrFetchHeaders(headers),
      body: JSON.stringify(payload || {}),
      timeoutMs,
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to create PDF download link');
  }
  return resolveSalonReportPdfDownloadUrl(data, API_BASE_URL);
}

/** @deprecated DOM screenshot capture — prefer createSalonReportPdfDownloadUrl */
export async function createSalonReportPdfDownloadUrlFromCapture({
  rootEl,
  sessionId,
  token,
  name = 'Guest',
  timeoutMs = 120000,
} = {}) {
  if (!rootEl) throw new Error('Report layout not ready');
  if (!sessionId) throw new Error('session_id is required');

  const pages = await captureReportPageImages(rootEl, {
    scale: 1.75,
    jpegQuality: 0.88,
    maxPages: 24,
  });

  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';
  const response = await fetchWithTimeout(
    `${API_BASE_URL}${SALON_API_PREFIX}/generate-report-pdf-from-capture`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        app_session_token: token || null,
        name,
        pages,
      }),
      timeoutMs,
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to create PDF download link');
  }
  return resolveSalonReportPdfDownloadUrl(data, API_BASE_URL);
}
